import { useState, type ReactNode } from "react";
import { useAuthState } from "../../../auth/AuthStateProvider";
import { logConnectorFailure, logConnectorSuccess } from "../../../telemetry";
import type { AttachmentInfo, CalEvent } from "../types";
import { fetchAttachmentFile } from "../services/eventDetail";
import type { MeetingThread } from "../services/meetingThread";
import { openEmailInOutlook, openInOutlook } from "../services/outlook";
import { saveFile } from "../services/saveFile";
import { DocIcon, DownloadIcon, MailIcon } from "./CalendarIcons";
import styles from "./CalendarModule.module.css";

/** A file's kind from its content type, first match wins. */
const FILE_KINDS: readonly (readonly [RegExp, string])[] = [
  [/word/i, "Word document"],
  [/sheet|excel/i, "Spreadsheet"],
  [/presentation|powerpoint/i, "Presentation"],
  [/pdf/i, "PDF"],
];

/** "Word document · 240 KB". */
function attachmentDetail(a: AttachmentInfo): string {
  const kind = FILE_KINDS.find(([re]) => re.test(a.contentType))?.[1] ?? "File";
  const size = a.size >= 1024 * 1024 ? `${(a.size / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(a.size / 1024))} KB`;
  return `${kind} · ${size}`;
}

/**
 * Whether the pane can save this file itself. Microsoft documents two places
 * where a download from an add-in silently does nothing: anything in Outlook
 * for Mac, and PDFs in Outlook for Windows (WebView2). There the meeting is
 * opened in Outlook instead, where the file can be saved.
 */
function paneCanSave(a: AttachmentInfo): boolean {
  const platform = Office.context?.diagnostics?.platform;
  if (platform === Office.PlatformType.Mac) return false;
  const pdf = /pdf/i.test(a.contentType) || /\.pdf$/i.test(a.name);
  return !(pdf && platform === Office.PlatformType.PC);
}

type DownloadState = "downloading" | "failed";

interface MeetingAttachmentsProps {
  readonly event: CalEvent;
  readonly token: string | null;
  /** null while loading; [] when there are none or they couldn't be read. */
  readonly attachments: readonly AttachmentInfo[] | null;
  /** The meeting's email thread, once found; it opens in Outlook. */
  readonly thread?: MeetingThread;
}

/**
 * Linked Material: the files attached to the invite. A file downloads; an
 * attached email or meeting, or a OneDrive/SharePoint link, opens the meeting
 * in Outlook, since Graph doesn't hand those over as a file.
 */
export function MeetingAttachments({ event, token, attachments, thread }: Readonly<MeetingAttachmentsProps>) {
  const { refreshGraphToken } = useAuthState();
  const [states, setStates] = useState<Readonly<Record<string, DownloadState>>>({});
  // Nothing to link: no attachments on the invite, and no email thread found.
  if (!event.hasAttachments && !thread) return null;

  const setState = (id: string, state: DownloadState | null) =>
    setStates((current) => {
      const next = { ...current };
      if (state) next[id] = state;
      else delete next[id];
      return next;
    });

  const download = async (a: AttachmentInfo) => {
    if (!token || states[a.id] === "downloading") return;
    setState(a.id, "downloading");
    try {
      const blob = await fetchAttachmentFile(token, () => refreshGraphToken(false), event.id, a.id);
      // Graph serves the bytes as a generic stream; the attachment's own type names the file correctly.
      saveFile(a.contentType ? new Blob([blob], { type: a.contentType }) : blob, a.name);
      setState(a.id, null);
      logConnectorSuccess("calendar", "download-attachment");
    } catch (e: unknown) {
      setState(a.id, "failed");
      logConnectorFailure("calendar", e, "download-attachment");
    }
  };

  let list: ReactNode;
  if (attachments === null) {
    list = <div className={styles.skel} />;
  } else if (attachments.length === 0 && event.hasAttachments) {
    list = <span className={styles.hint}>The attachments couldn't be listed. Open the meeting in Outlook to see them.</span>;
  } else {
    list = attachments.map((a) => {
      const downloadable = a.kind === "file" && a.id !== "" && token !== null && paneCanSave(a);
      const state = states[a.id];
      let desc: string;
      if (!downloadable && a.kind === "file") desc = `${attachmentDetail(a)} · Open in Outlook to save`;
      else if (!downloadable) desc = `${a.kind === "reference" ? "Cloud file" : "Outlook item"} · Open in Outlook`;
      else if (state === "downloading") desc = `${attachmentDetail(a)} · Downloading…`;
      else if (state === "failed") desc = "Couldn't download — select to try again";
      else desc = attachmentDetail(a);
      return (
        <button
          key={a.id || a.name}
          type="button"
          className={styles.opt}
          onClick={() => {
            if (downloadable) void download(a);
            else openInOutlook(event);
          }}
          aria-label={downloadable ? `Download ${a.name}, ${desc}` : `${a.name}, ${desc}`}
          aria-busy={state === "downloading"}
        >
          <span className={styles.optKey}>
            <DocIcon />
          </span>
          <span className={styles.optBody}>
            <span className={styles.optName}>{a.name}</span>
            <span className={`${styles.optDesc} ${state === "failed" ? styles.busyNote : ""}`}>{desc}</span>
          </span>
          {downloadable && (
            <span className={styles.optAction}>
              <DownloadIcon />
            </span>
          )}
        </button>
      );
    });
  }

  return (
    <div className={styles.sec}>
      <div className={styles.secH}>
        <span className={styles.secBar} />
        <span className={styles.secTitle}>Linked Material</span>
        {attachments && <span className={`${styles.secRight} ${styles.secMeta}`}>{attachments.length + (thread ? 1 : 0)}</span>}
      </div>
      {list}
      {thread && <ThreadRow thread={thread} />}
    </div>
  );
}

/** The meeting's email thread, as in the mockup ("Nick Morgan thread · Mail thread"); it opens in Outlook. */
function ThreadRow({ thread }: Readonly<{ thread: MeetingThread }>) {
  const name = `${thread.sender} thread`;
  return (
    <button
      type="button"
      className={styles.opt}
      onClick={() => openEmailInOutlook(thread.messageId, thread.webLink)}
      aria-label={`${name}, mail thread, opens in Outlook`}
    >
      <span className={styles.optKey}>
        <MailIcon />
      </span>
      <span className={styles.optBody}>
        <span className={styles.optName}>{name}</span>
        <span className={styles.optDesc}>Mail thread</span>
      </span>
    </button>
  );
}
